import { getDeploymentsApi, type Deployment } from "../api";
import { callApiObservable } from "@/api";
import { getInstalledAppApi, type InstalledApp } from "@/api/appApi";
import {
  batchSetMessageStatusApi,
  completeConversationApi,
  createConversationApi,
  sendMessageApi,
} from "@/api/messagingApi";
import type { ChatboxProps } from "@/components/chat/Chatbox";
import { toIResponseError } from "@/model/error";
import {
  MessagingChannelType,
  type MessagingConversation,
  MessagingEventType,
  MessagingMessageType,
  type MessagingParticipant,
  MessagingParticipantType,
  type MessagingPayload,
  type MessagingStatus,
} from "@/model/messaging";
import {
  atomRequired,
  BaseStore,
  createAtomStore,
} from "@deputy-ai/common/jotai";
import { produce } from "immer";
import { type Atom, atom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import { catchError, defer, EMPTY, finalize, map, of, switchMap } from "rxjs";
import { v4 as uuidv4 } from "uuid";

export const DEFAULT_PARTICIPANT_KEY = "default";

export abstract class AbstractConversationsStore extends BaseStore {
  summarize?: ChatboxProps["summarize"];

  participants = atomWithStorage(
    "DeputyAI:AIAgent:User",
    {
      [DEFAULT_PARTICIPANT_KEY]: {
        name: "Anonymous",
        email: "",
        externalId: uuidv4(),
        type: MessagingParticipantType.Visitor,
      },
    },
    createJSONStorage<Record<string, MessagingParticipant>>()
  );

  deploymentParticipant = atom((get) => {
    const deployment = get(this.deployment);
    const participants = get(this.participants);
    if (!deployment?.uid) {
      return participants[DEFAULT_PARTICIPANT_KEY];
    }
    return (
      participants[deployment.uid] || participants[DEFAULT_PARTICIPANT_KEY]
    );
  });

  myself = this.deploymentParticipant;

  storagedConversation = atomWithStorage<
    Record<string, MessagingConversation | undefined>
  >("DeputyAI:AIAgent:Conversation", {});

  storagedDeploymentConversation = atom((get) => {
    const deployment = get(this.deployment);
    const conversations = get(this.storagedConversation);
    return deployment && conversations[deployment.uid!];
  });

  messagingApp = atom<InstalledApp>();

  creating = atom(false);

  abstract deployment: Atom<Deployment | undefined>;

  deploymentUid = atom((get) => get(this.deployment)?.uid);

  deploymentRequired = atomRequired(
    (get) => get(this.deployment),
    "deployment is required"
  );

  deploymentError = atom<string>();

  params = atom((get) => ({
    orgUid: get(this.deployment)?.orgUid ?? "",
    installedAppUid: get(this.deployment)?.installedAppUid ?? "",
  }));

  pendingVisitorMessage = atom<string>();

  conversation: Atom<MessagingConversation | undefined> =
    atom<MessagingConversation>();

  conversationUid = atom((get) => {
    const conversation = get(this.conversation);
    return conversation?.uid;
  });

  deploymentWebchatSettings = atom((get) => {
    const deployment = get(this.deployment);
    if (!deployment) {
      return undefined;
    }
    return deployment.config?.webchat;
  });

  setParticipant(participant: MessagingParticipant) {
    const deployment = this.get(this.deployment);
    this.set(this.participants, (prev) =>
      produce(prev, (draft) => {
        if (deployment?.uid) {
          draft[deployment.uid] = participant;
        }
      })
    );
  }

  setPendingVisitorMessage(message: string) {
    this.set(this.pendingVisitorMessage, message);
  }

  setStoragedConversation(conversation?: MessagingConversation) {
    const deployment = this.get(this.deployment);
    this.set(this.storagedConversation, (prev) =>
      produce(prev, (draft) => {
        if (deployment?.uid) {
          draft[deployment.uid] = conversation;
        }
      })
    );
  }

  endConversation() {
    const conversation = this.get(this.conversation);
    const deploymentUid = this.get(this.deploymentUid);
    if (!conversation || !deploymentUid) {
      return of();
    }

    this.set(this.storagedConversation, (prev) =>
      produce(prev, (draft) => {
        draft[deploymentUid] = undefined;
      })
    );

    return callApiObservable(async () => {
      return await completeConversationApi({
        path: {
          orgUid: conversation.orgUid,
          installedAppUid: conversation.installedAppUid,
          conversationUid: conversation.uid,
        },
        body: {
          orgUid: conversation.orgUid,
          installedAppUid: conversation.installedAppUid,
          conversationUid: conversation.uid,
        },
      });
    }).pipe(map(() => conversation));
  }

  buildDefaultMessage(conversationUid: string) {
    return {
      ...this.get(this.params),
      conversationUid,
      eventType: MessagingEventType.CustomerMessage,
      payload: {
        message: "",
        messageType: MessagingMessageType.Text,
        channel: MessagingChannelType.Web,
      },
      participant: this.get(this.myself),
      eventTime: new Date().toISOString(),
    };
  }

  batchSetMessageStatus(args: {
    deliveredStatus: Record<string, MessagingStatus>;
    messageUids: string[];
  }) {
    const { deliveredStatus, messageUids } = args;

    return defer(() => {
      const conversation = this.get(this.conversation);
      if (!conversation) {
        throw new Error("Conversation not found");
      }

      return callApiObservable(async ({ controller }) => {
        return await batchSetMessageStatusApi({
          signal: controller.signal,
          path: {
            orgUid: conversation.orgUid,
            installedAppUid: conversation.installedAppUid,
            conversationUid: conversation.uid,
          },
          body: {
            orgUid: conversation.orgUid,
            installedAppUid: conversation.installedAppUid,
            conversationUid: conversation.uid,
            messageUids,
            deliveredStatus,
          },
        });
      });
    });
  }

  createConversationAndSendMessage(message: string) {
    return this.createConversation().pipe(
      map((conversation) => {
        if (conversation) {
          void this.sendMessage(conversation.uid, {
            message,
            messageType: MessagingMessageType.Text,
            channel: MessagingChannelType.Web,
          });
        }
        return conversation;
      })
    );
  }

  createConversation() {
    this.set(this.creating, true);
    const deployment = this.get(this.deployment);
    if (!deployment?.uid) {
      return of();
    }

    const participant = this.get(this.myself);
    return callApiObservable(async () => {
      const params = this.get(this.params);
      return await createConversationApi({
        path: {
          ...params,
        },
        body: {
          ...params,
          participants: [participant],
          skipWelcomeMessage: true,
        },
      });
    }).pipe(
      map((res) => {
        return res.conversation!;
      }),
      map((conversation) => {
        const deployment = this.get(this.deployment);
        this.set(this.storagedConversation, (prev) =>
          produce(prev, (draft) => {
            if (deployment) {
              draft[deployment.uid!] = conversation;
            }
          })
        );
        this.set(this.participants, (prev) =>
          produce(prev, (draft) => {
            draft[deployment!.uid!] = participant;
          })
        );
        return conversation;
      }),
      finalize(() => this.set(this.creating, false))
    );
  }

  sendMessage(conversationUid: string, payload: MessagingPayload) {
    return callApiObservable(async ({ controller }) => {
      const message = this.buildDefaultMessage(conversationUid);
      message.payload = payload;

      return await sendMessageApi({
        signal: controller.signal,
        path: {
          ...message,
        },
        body: {
          ...message,
          message,
        },
      });
    });
  }
}
export class ConversationsStore extends AbstractConversationsStore {
  deployment = atom<Deployment>();
  conversation = atom((get) => {
    const deployment = get(this.deployment);
    if (!deployment) {
      return undefined;
    }
    const conversations = get(this.storagedConversation);
    return conversations[deployment.uid!];
  });

  init(orgUid: string, name: string) {
    return this.loadDeployment(orgUid, name).pipe(
      switchMap((res) => {
        if (res?.orgUid && res.installedAppUid) {
          return this.loadMessagingApp(res.orgUid, res.installedAppUid);
        }
        return EMPTY;
      })
    );
  }

  loadDeployment(orgUid: string, name: string) {
    this.set(this.deploymentError, "");
    return callApiObservable(
      async ({ controller }) =>
        await getDeploymentsApi({
          signal: controller.signal,
          path: { orgUid, name },
        })
    ).pipe(
      catchError((err: unknown) => {
        this.set(this.deploymentError, toIResponseError(err).detail);
        return of();
      }),
      map((res) => {
        const { deployment } = res;
        this.set(this.deployment, deployment as Deployment);
        return deployment;
      })
    );
  }

  loadMessagingApp(orgUid: string, installedAppUid: string) {
    return callApiObservable(async () => {
      return await getInstalledAppApi({
        path: { orgUid, installedAppUid },
      });
    }).pipe(map((res) => this.set(this.messagingApp, res.app)));
  }
}

export const { useConversationsStore, ConversationsProvider } =
  createAtomStore<AbstractConversationsStore>("Conversations");
